// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useBunzlDispatch } from "@store/Framework/Store/Bunzl/BunzlReducer";
import StepFormInputContext from "@store/Framework/Store/Context/StepFormInputContext";
import StepFormRadioButtons from "@store/Library/Components/Bunzl/StepFormComponents/StepFormRadioButtons";
import StepFormTextField from "@store/Library/Components/Bunzl/StepFormComponents/StepFormTextField";
import {
    CreditFormStepProps,
    creditFormStepStyles,
} from "@store/Library/Components/CreditForm/StepComponents/BasicDetailsForm";
import FadeIn from "@store/Library/Components/Reporting/Common/FadeIn";
import CardBodyComponent from "@store/Library/Widgets/Card/CardBody";
import Radio from "@store/MobiusOverrides/Radio/Radio";
import { toToolkitApplicationState } from "@toolkit/Framework/Store/ApplicationState";
import React from "react";
import styled, { css } from "styled-components";

const CenteredDivider = styled.div`
    text-align: center;
    position: relative;
    top: 2px;
    padding-top: 1px;
    line-height: 0;
    width: 100%;
    &:after {
        content: "";
        width: 100%;
        background-color: transparent;
        display: block;
        height: 1px;
        border-top: 1px solid ${getColor("text.accent")};
        position: absolute;
        top: 50%;
        margin-top: -1px;
        z-index: 1;
    }
`;

interface PrincipalAndTaxInformationFormStyles {
    fullWidthWrapper?: InjectableCss;
    sectionTitle?: InjectableCss;
}

const principalAndTaxInformationFormStyles: PrincipalAndTaxInformationFormStyles = {
    fullWidthWrapper: {
        css: css`
            width: 100%;
        `,
    },
    sectionTitle: {
        css: css`
            margin-top: 0;
            margin-bottom: 0;
            width: 100%;
        `,
    },
};
const PrincipalAndTaxInformationForm: React.FC<CreditFormStepProps> = ({
    hasSubmitted,
    editable = true,
    stepIndex,
}) => {
    const bunzlDispatch = useBunzlDispatch();
    const styles = creditFormStepStyles;

    const handleNoClick = (inputType: string) => {
        bunzlDispatch({
            type: "Data/StepForm/RemoveInput",
            stepIndex: stepIndex ?? 0,
            formKey: "creditForm",
            inputKey: inputType,
        });
    };
    const organizationPSTExempt = useAppSelector(
        state => toToolkitApplicationState(state).bunzl.pages.stepForm?.creditForm?.steps[1]?.inputs?.PSTExempt,
    )?.value;
    const gstExempt = useAppSelector(
        state => toToolkitApplicationState(state).bunzl.pages.stepForm?.creditForm?.steps[1]?.inputs?.GSTExempt,
    )?.value;

    const stepFormInputContextData = React.useMemo(
        () => ({ currentStep: stepIndex, editable, hasSubmitted }),
        [stepIndex, editable, hasSubmitted],
    );
    return (
        <StepFormInputContext.Provider value={stepFormInputContextData}>
            <CardBodyComponent extendedStyles={styles.cardBody}>
                <Typography variant="h4" {...principalAndTaxInformationFormStyles.sectionTitle}>
                    {translate("Principal Information")}
                </Typography>

                <StepFormTextField
                    hasSubmitted={hasSubmitted}
                    inputKey="principalName"
                    validation="name"
                    label={translate("Name")}
                    placeholder={translate("Enter Principal Name")}
                    disabled={!editable}
                    stepOverride={stepIndex}
                />
                <StepFormTextField
                    hasSubmitted={hasSubmitted}
                    inputKey="principalTitle"
                    label={translate("Title")}
                    validation="title"
                    placeholder={translate("Enter Title")}
                    disabled={!editable}
                    stepOverride={stepIndex}
                />

                <StepFormTextField
                    hasSubmitted={hasSubmitted}
                    labelProps={{ size: 14 }}
                    inputKey="principalAddress"
                    validation="companyAddress"
                    label={translate("Residence Address")}
                    placeholder={translate("Enter Address")}
                    disabled={!editable}
                    stepOverride={stepIndex}
                />

                <StepFormTextField
                    validation="phoneNumber"
                    stepOverride={stepIndex}
                    hasSubmitted={hasSubmitted}
                    inputKey="principalPhoneNumber"
                    label={translate("Residence Phone Number")}
                    placeholder={translate("Enter Phone Number")}
                    autoComplete="tel"
                    type="tel"
                    disabled={!editable}
                />
                <CenteredDivider />
                <Typography variant="h4" {...principalAndTaxInformationFormStyles.sectionTitle}>
                    {translate("Tax Information")}
                </Typography>
                <StepFormRadioButtons
                    {...creditFormStepStyles.radioGroup}
                    label={translate("Is your organization exempt from paying GST?")}
                    inputKey="GSTExempt"
                    disabled={!editable}
                    stepOverride={stepIndex}
                    required
                    hasSubmitted={hasSubmitted}
                    validation="gstExcempt"
                >
                    <Radio value="Yes">{translate("Yes")}</Radio>
                    <Radio onClick={() => handleNoClick("GstExemptionNumber")} value="No">
                        {translate("No")}
                    </Radio>
                </StepFormRadioButtons>
                {gstExempt === "Yes" && (
                    <StyledWrapper {...principalAndTaxInformationFormStyles.fullWidthWrapper}>
                        <FadeIn duration={300} animate appear>
                            <StepFormTextField
                                validation="excemptionNumber"
                                hasSubmitted={hasSubmitted}
                                required
                                labelProps={{ size: 14 }}
                                inputKey="GstExemptionNumber"
                                label={translate("GST Exemption Number")}
                                placeholder={translate("Enter Exemption Number")}
                                disabled={!editable}
                                stepOverride={stepIndex}
                            />
                        </FadeIn>
                    </StyledWrapper>
                )}
                <StepFormRadioButtons
                    {...creditFormStepStyles.radioGroup}
                    label={translate("Is your organization exempt from paying PST?")}
                    inputKey="PSTExempt"
                    disabled={!editable}
                    stepOverride={stepIndex}
                    required
                    hasSubmitted={hasSubmitted}
                    validation="pstExcempt"
                >
                    <Radio value="Yes">{translate("Yes")}</Radio>
                    <Radio onClick={() => handleNoClick("PstExemptionNumber")} value="No">
                        {translate("No")}
                    </Radio>
                </StepFormRadioButtons>
                {organizationPSTExempt === "Yes" && (
                    <StyledWrapper {...principalAndTaxInformationFormStyles.fullWidthWrapper}>
                        <FadeIn duration={300} animate appear>
                            <StepFormTextField
                                validation="excemptionNumber"
                                hasSubmitted={hasSubmitted}
                                required
                                labelProps={{ size: 14 }}
                                inputKey="PstExemptionNumber"
                                label={translate("PST Exemption Number")}
                                placeholder={translate("Enter Exemption Number")}
                                disabled={!editable}
                                stepOverride={stepIndex}
                            />
                        </FadeIn>
                    </StyledWrapper>
                )}
            </CardBodyComponent>
        </StepFormInputContext.Provider>
    );
};

export default PrincipalAndTaxInformationForm;
